export const HTML_PREVIEW_SOURCE_ID_ATTR = "data-decoda-source-id";

export interface HtmlPreviewSourceRange {
  id: string;
  tagName: string;
  start: number;
  end: number;
}

const VOID_TAGS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

const FOREIGN_CONTENT_TAGS = new Set(["math", "svg"]);
const RAW_TEXT_TAGS = new Set(["script", "style", "textarea", "title"]);

interface OpenElement {
  tagName: string;
  range: HtmlPreviewSourceRange;
}

interface AttributeInsertion {
  index: number;
  value: string;
}

function isTagNameChar(char: string): boolean {
  return /[A-Za-z0-9:-]/.test(char);
}

function isTagNameStartChar(char: string | undefined): boolean {
  return !!char && /[A-Za-z]/.test(char);
}

function readTagName(html: string, index: number): string | null {
  if (!isTagNameStartChar(html[index])) return null;
  let cursor = index + 1;
  while (cursor < html.length && isTagNameChar(html[cursor])) cursor++;
  return html.slice(index, cursor).toLowerCase();
}

/** Index just past the `>` that closes the tag opened at `start`, or -1. Quote-aware. */
export function findTagEnd(html: string, start: number): number {
  let quote: '"' | "'" | null = null;

  for (let cursor = start + 1; cursor < html.length; cursor++) {
    const char = html[cursor];
    if (quote) {
      if (char === quote) quote = null;
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      continue;
    }
    if (char === ">") return cursor + 1;
  }

  return -1;
}

function getAttributeInsertionIndex(html: string, tagEnd: number): number {
  let cursor = tagEnd - 2;
  while (cursor >= 0 && /\s/.test(html[cursor])) cursor--;
  return html[cursor] === "/" ? cursor : tagEnd - 1;
}

function isSelfClosingTag(
  html: string,
  tagEnd: number,
  tagName: string,
  isInForeignContent: boolean,
): boolean {
  if (VOID_TAGS.has(tagName)) return true;
  if (!isInForeignContent && !FOREIGN_CONTENT_TAGS.has(tagName)) return false;

  let cursor = tagEnd - 2;
  while (cursor >= 0 && /\s/.test(html[cursor])) cursor--;
  return html[cursor] === "/";
}

function findRawTextClosingTagStart(
  html: string,
  start: number,
  tagName: string,
): number {
  const lowerHtml = html.toLowerCase();
  const closeTagPrefix = `</${tagName}`;
  let cursor = start;

  while (cursor < html.length) {
    const closeTagStart = lowerHtml.indexOf(closeTagPrefix, cursor);
    if (closeTagStart === -1) return -1;

    const nextChar = html[closeTagStart + closeTagPrefix.length];
    if (!isTagNameChar(nextChar ?? "")) return closeTagStart;

    cursor = closeTagStart + closeTagPrefix.length;
  }

  return -1;
}

function applyInsertions(
  html: string,
  insertions: AttributeInsertion[],
): string {
  return [...insertions]
    .sort((a, b) => b.index - a.index)
    .reduce(
      (nextHtml, insertion) =>
        `${nextHtml.slice(0, insertion.index)}${insertion.value}${nextHtml.slice(insertion.index)}`,
      html,
    );
}

export function buildHtmlPreviewSourceMap(html: string): {
  html: string;
  ranges: HtmlPreviewSourceRange[];
} {
  const ranges: HtmlPreviewSourceRange[] = [];
  const insertions: AttributeInsertion[] = [];
  const openElements: OpenElement[] = [];
  let cursor = 0;
  let nextId = 1;

  while (cursor < html.length) {
    if (html[cursor] !== "<") {
      cursor++;
      continue;
    }

    if (html.startsWith("<!--", cursor)) {
      const commentEnd = html.indexOf("-->", cursor + 4);
      cursor = commentEnd === -1 ? html.length : commentEnd + 3;
      continue;
    }

    if (html.startsWith("<![CDATA[", cursor)) {
      const cdataEnd = html.indexOf("]]>", cursor + 9);
      cursor = cdataEnd === -1 ? html.length : cdataEnd + 3;
      continue;
    }

    const nextChar = html[cursor + 1];
    if (nextChar === "!" || nextChar === "?") {
      const tagEnd = findTagEnd(html, cursor);
      cursor = tagEnd === -1 ? html.length : tagEnd;
      continue;
    }

    if (nextChar === "/") {
      const tagName = readTagName(html, cursor + 2);
      const tagEnd = findTagEnd(html, cursor);
      if (!tagName || tagEnd === -1) {
        cursor++;
        continue;
      }

      while (openElements.length > 0) {
        const openElement = openElements.pop();
        if (!openElement) break;
        openElement.range.end =
          openElement.tagName === tagName ? tagEnd : cursor;
        if (openElement.tagName === tagName) break;
      }

      cursor = tagEnd;
      continue;
    }

    const tagName = readTagName(html, cursor + 1);
    const tagEnd = findTagEnd(html, cursor);
    if (!tagName || tagEnd === -1) {
      cursor++;
      continue;
    }

    const id = `html-source-${nextId++}`;
    const range: HtmlPreviewSourceRange = {
      id,
      tagName,
      start: cursor,
      end: tagEnd,
    };
    ranges.push(range);
    insertions.push({
      index: getAttributeInsertionIndex(html, tagEnd),
      value: ` ${HTML_PREVIEW_SOURCE_ID_ATTR}="${id}"`,
    });

    if (RAW_TEXT_TAGS.has(tagName)) {
      const closeTagStart = findRawTextClosingTagStart(html, tagEnd, tagName);
      if (closeTagStart === -1) {
        range.end = html.length;
        cursor = html.length;
      } else {
        const closeTagEnd = findTagEnd(html, closeTagStart);
        range.end = closeTagEnd === -1 ? html.length : closeTagEnd;
        cursor = range.end;
      }
      continue;
    }

    const isInForeignContent = openElements.some((openElement) =>
      FOREIGN_CONTENT_TAGS.has(openElement.tagName),
    );

    if (!isSelfClosingTag(html, tagEnd, tagName, isInForeignContent)) {
      openElements.push({ tagName, range });
    }

    cursor = tagEnd;
  }

  for (const openElement of openElements) {
    openElement.range.end = html.length;
  }

  return {
    html: applyInsertions(html, insertions),
    ranges,
  };
}

export function findHtmlPreviewSourceRangeAtOffset(
  ranges: HtmlPreviewSourceRange[],
  offset: number,
): HtmlPreviewSourceRange | null {
  let match: HtmlPreviewSourceRange | null = null;

  for (const range of ranges) {
    if (offset < range.start || offset >= range.end) continue;
    if (!match || range.end - range.start < match.end - match.start) {
      match = range;
    }
  }

  return match;
}
